import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useScopedPath } from './ScopedLink';

/**
 * "Back" for a leaf page, safe for deep links.
 *
 * A plain `navigate(-1)` (or `window.history.back()`) is wrong here, and the
 * reason is live in this product: parents arrive at detail pages from a PUSH
 * NOTIFICATION, and staff from bookmarks and refreshes. In all three cases the
 * page is the FIRST entry in the session's history, so there is nothing to pop
 * — back either does nothing at all or throws the user out of the app entirely.
 *
 * react-router exposes a numeric `idx` on `location.state` (v6+). When it is 0
 * or absent we are on the first entry, so we navigate to an explicit fallback
 * instead of popping. Otherwise we pop, which preserves whatever list filters
 * the user had set — the thing a hardcoded link loses.
 *
 * This logic was written twice, verbatim, in MessageDetailPage and
 * ObservationDetailPage, while IncidentDetail used a bare `history.back()`
 * (carrying the deep-link bug) and StaffComplianceProfile used a plain link
 * (always discarding filters). One component, so the next leaf page cannot
 * pick the broken variant.
 */
export interface BackLinkProps {
  /**
   * Where to go when there is no history to pop — a deep link, a bookmark, or
   * a refresh. Scope-relative: pass '/policies', not '/loc/abc/policies'. The
   * current org/location prefix is applied for you, exactly as ScopedLink does,
   * so an org admin does not get dropped out of their location.
   */
  fallbackTo: string;
  /** Defaults to "Back". Use something specific where it helps. */
  label?: string;
  /** Required, so every instance is addressable from a Playwright test. */
  'data-testid': string;
  className?: string;
}

export const BackLink: React.FC<BackLinkProps> = ({
  fallbackTo,
  label = 'Back',
  className,
  ...rest
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scopedPath = useScopedPath();

  const goBack = React.useCallback(() => {
    const idx = (location.state as { idx?: number } | null)?.idx;
    if (idx === undefined || idx === 0) {
      navigate(scopedPath(fallbackTo));
    } else {
      navigate(-1);
    }
  }, [navigate, location.state, scopedPath, fallbackTo]);

  return (
    <button
      type="button"
      data-testid={rest['data-testid']}
      onClick={goBack}
      className={
        className ?? 'inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700'
      }
    >
      <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
      {label}
    </button>
  );
};
